import React from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper,
  useTheme,
  useMediaQuery 
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import HomeIcon from '@mui/icons-material/Home';
import GroupIcon from '@mui/icons-material/Group';
import { useNavigate } from 'react-router';

const MonetizationBanner = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Paper
      style={{cursor: "pointer", margin: "0 auto"}}
      onClick={() => navigate("/monetizationLP")}
      elevation={3}
      sx={{
        width: '100%',
        maxWidth: "1000px",
        minHeight: { xs: '160px', sm: '180px' },
        maxHeight: { xs: '200px', sm: '230px' },
        background: 'linear-gradient(to right, #F8FAFC, #F1F5F9)',
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          height: '100%',
          px: { xs: 1.5, sm: 3, md: 4 },
          py: { xs: 1.25, sm: 1.75, md: 2.25 },
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: { xs: 1.5, sm: 2, md: 3 }
        }}
      >
        {/* Text Content */}
        <Box sx={{ 
          flex: 1,
          minWidth: 0,
          maxWidth: { xs: '65%', sm: '75%', md: 'none' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-start',
          height: '100%'
        }}>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h2" 
            sx={{
              mb: { xs: 0.5, sm: 0.75 },
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.875rem' },
              lineHeight: { xs: 1.2, sm: 1.3 },
              fontWeight: 'bold'
            }}
          >
            創作活動を
            <Box
              component="span"
              sx={{
                background: 'linear-gradient(to right, #FFD700, #FF8C00)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                whiteSpace: 'nowrap',
                ml: 0.5
              }}
            >
              収益化
            </Box>
            しよう
          </Typography>

          {/* Benefits List */}
          <Box
            sx={{
              mb: { xs: 0.5, sm: 0.75 },
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: 0.25, sm: 0.5 }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <MonetizationOnIcon 
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  color: '#FFD700'
                }} 
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                作品評価による活動支援金
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <HomeIcon 
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  color: '#4CAF50'
                }} 
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                プランを作成して月次・単品販売
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <GroupIcon 
                sx={{ 
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  color: '#FF6B6B'
                }} 
              />
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'text.primary',
                  fontWeight: 500
                }}
              >
                アフィリエイト紹介報酬
              </Typography>
            </Box>
          </Box>

          <Button
            variant="contained"
            sx={{
              background: 'linear-gradient(to right, #FFD700, #FF8C00)',
              borderRadius: 25,
              textTransform: 'none',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              py: { xs: 0.75, sm: 1 },
              px: { xs: 2, sm: 3 },
              minWidth: { xs: '100px', sm: '120px' },
              color: '#333',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
              alignSelf: 'flex-start',
              '&:hover': {
                background: 'linear-gradient(to right, #FF8C00, #FF6B00)',
                boxShadow: '0 6px 16px rgba(255, 140, 0, 0.4)',
                transform: 'translateY(-1px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            詳細を見る
          </Button>
        </Box>

        {/* Feature Icons */}
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            flexShrink: 0,
            minWidth: { xs: '30%', sm: '25%' },
            height: '100%',
            pt: { xs: 0.5, sm: 1 }
          }}
        >
          {/* Feature Cards */}
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', md: 'row' },
              gap: { xs: 0.4, sm: 0.6, md: 0.8 },
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Box
              sx={{
                width: { xs: 36, sm: 50, md: 55 },
                height: { xs: 36, sm: 50, md: 55 },
                borderRadius: 2,
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(255, 215, 0, 0.3)',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              <MonetizationOnIcon
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.5rem', md: '1.625rem' },
                  color: 'white'
                }}
              />
            </Box>

            <Box
              sx={{
                width: { xs: 36, sm: 50, md: 55 },
                height: { xs: 36, sm: 50, md: 55 },
                borderRadius: 2,
                background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              <HomeIcon
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.5rem', md: '1.625rem' },
                  color: 'white'
                }}
              />
            </Box>

            <Box
              sx={{
                width: { xs: 36, sm: 50, md: 55 },
                height: { xs: 36, sm: 50, md: 55 },
                borderRadius: 2,
                background: 'linear-gradient(135deg, #FF6B6B 0%, #FF8A80 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)'
                }
              }}
            >
              <GroupIcon
                sx={{
                  fontSize: { xs: '1.125rem', sm: '1.5rem', md: '1.625rem' },
                  color: 'white'
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default MonetizationBanner;